import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api, TOKEN_KEY } from '../api/client'
import type { AuthResponse, Role, User } from '../types'

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem(TOKEN_KEY) || '')
  const user = ref<User | null>(null)
  const ready = ref(false)
  const isAuthenticated = computed(() => Boolean(token.value && user.value))
  const isAdmin = computed(() => user.value?.role === 'admin')

  function accept(response: AuthResponse) {
    token.value = response.token
    user.value = response.user
    localStorage.setItem(TOKEN_KEY, response.token)
  }

  async function login(username: string, password: string, role: Role = 'user') {
    const endpoint = role === 'admin' ? '/auth/admin/login' : '/auth/login'
    const { data } = await api.post<AuthResponse>(endpoint, { username, password })
    accept(data)
  }

  async function register(payload: {
    username: string
    real_name: string
    employee_no?: string
    password: string
  }) {
    await api.post('/auth/register', payload)
    await login(payload.username, payload.password)
  }

  async function restore() {
    if (!token.value) {
      ready.value = true
      return
    }
    try {
      const { data } = await api.get<User>('/auth/me')
      user.value = data
    } catch {
      clear()
    } finally {
      ready.value = true
    }
  }

  async function logout() {
    try {
      if (token.value) await api.post('/auth/logout')
    } finally {
      clear()
    }
  }

  function clear() {
    token.value = ''
    user.value = null
    localStorage.removeItem(TOKEN_KEY)
  }

  return { token, user, ready, isAuthenticated, isAdmin, login, register, restore, logout, clear }
})
